import { useEffect, useRef, useState } from "react";
import { Download, Copy, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { moodById } from "@/lib/constants";
import { imageUrl } from "@/lib/api";

function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines = 12) {
  const paragraphs = text.split("\n");
  let cy = y;
  let lines = 0;
  for (const para of paragraphs) {
    const words = para.split(" ");
    let line = "";
    for (let i = 0; i < words.length; i++) {
      const test = line ? line + " " + words[i] : words[i];
      if (ctx.measureText(test).width > maxWidth && line) {
        ctx.fillText(line, x, cy);
        line = words[i];
        cy += lineHeight;
        lines++;
        if (lines >= maxLines - 1) {
          ctx.fillText(line + "…", x, cy);
          return cy;
        }
      } else {
        line = test;
      }
    }
    if (line) {
      ctx.fillText(line, x, cy);
      cy += lineHeight;
      lines++;
      if (lines >= maxLines) return cy;
    }
  }
  return cy;
}

export default function ShareCardDialog({ open, onOpenChange, post }) {
  const canvasRef = useRef(null);
  const [canvasNode, setCanvasNode] = useState(null);
  const [dataUrl, setDataUrl] = useState("");

  const setCanvasRef = (node) => {
    canvasRef.current = node;
    setCanvasNode(node);
  };

  useEffect(() => {
    if (!open || !post || !canvasNode) return;
    const c = canvasNode;
    const W = 1080;
    const H = 1080;
    c.width = W;
    c.height = H;
    const ctx = c.getContext("2d");

    const draw = (img) => {
      // background gradient
      const grad = ctx.createLinearGradient(0, 0, W, H);
      grad.addColorStop(0, "#0C0B0E");
      grad.addColorStop(1, "#15131A");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // ember glow blob
      const blob = ctx.createRadialGradient(W - 200, 200, 20, W - 200, 200, 500);
      blob.addColorStop(0, "rgba(255, 90, 54, 0.35)");
      blob.addColorStop(1, "rgba(255, 90, 54, 0)");
      ctx.fillStyle = blob;
      ctx.fillRect(0, 0, W, H);

      // header — brand
      ctx.fillStyle = "#FF5A36";
      ctx.font = "800 56px Outfit, sans-serif";
      ctx.fillText("twatter", 80, 140);
      ctx.fillStyle = "#6E6778";
      ctx.font = "500 22px Inter, sans-serif";
      ctx.fillText("VENT. DON'T BOTTLE IT.", 80, 180);

      // mood chip
      const m = moodById(post.mood);
      ctx.font = "600 24px Inter, sans-serif";
      const label = `${m.label.toUpperCase()} · ${post.category.toUpperCase()}`;
      const tw = ctx.measureText(label).width;
      const chipX = 80;
      const chipY = 260;
      const chipH = 56;
      const chipW = tw + 60;
      ctx.fillStyle = m.bg;
      const rr = 28;
      ctx.beginPath();
      ctx.moveTo(chipX + rr, chipY);
      ctx.arcTo(chipX + chipW, chipY, chipX + chipW, chipY + chipH, rr);
      ctx.arcTo(chipX + chipW, chipY + chipH, chipX, chipY + chipH, rr);
      ctx.arcTo(chipX, chipY + chipH, chipX, chipY, rr);
      ctx.arcTo(chipX, chipY, chipX + chipW, chipY, rr);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = m.color;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = m.color;
      ctx.fillText(label, chipX + 30, chipY + 37);

      let textY = 480;
      const hasImage = !!img;
      if (hasImage) {
        // draw image as a cover-fit rounded rect
        const ix = 80;
        const iy = 350;
        const iw = W - 160;
        const ih = 420;
        const ratio = img.width / img.height;
        const targetRatio = iw / ih;
        let sx = 0, sy = 0, sw = img.width, sh = img.height;
        if (ratio > targetRatio) {
          sw = img.height * targetRatio;
          sx = (img.width - sw) / 2;
        } else {
          sh = img.width / targetRatio;
          sy = (img.height - sh) / 2;
        }
        const ir = 24;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(ix + ir, iy);
        ctx.arcTo(ix + iw, iy, ix + iw, iy + ih, ir);
        ctx.arcTo(ix + iw, iy + ih, ix, iy + ih, ir);
        ctx.arcTo(ix, iy + ih, ix, iy, ir);
        ctx.arcTo(ix, iy, ix + iw, iy, ir);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(img, sx, sy, sw, sh, ix, iy, iw, ih);
        ctx.restore();
        ctx.strokeStyle = "#2D2838";
        ctx.lineWidth = 1;
        ctx.stroke();
        textY = 820;
      } else {
        // opening quote mark
        ctx.fillStyle = "rgba(255, 90, 54, 0.15)";
        ctx.font = "900 240px Outfit, sans-serif";
        ctx.fillText("\u201C", 60, 430);
      }

      // vent content
      ctx.fillStyle = "#F3F1F6";
      ctx.font = "600 44px Outfit, sans-serif";
      wrapText(ctx, post.content || "", 80, textY, W - 160, 60, hasImage ? 4 : 10);

      // footer — handle + brand
      ctx.fillStyle = "#6E6778";
      ctx.font = "500 22px JetBrains Mono, monospace";
      ctx.fillText(`— ${post.handle}`, 80, H - 100);
      ctx.fillStyle = "#A19BAA";
      ctx.font = "500 20px Inter, sans-serif";
      const foot = "twatter · anonymous catharsis";
      const fw = ctx.measureText(foot).width;
      ctx.fillText(foot, W - fw - 80, H - 100);

      try {
        setDataUrl(c.toDataURL("image/png"));
      } catch (e) {
        // tainted canvas if image cross-origin without CORS; fall back
        setDataUrl("");
      }
    };

    if (post.image_path) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => draw(img);
      img.onerror = () => draw(null);
      img.src = imageUrl(post.image_path);
    } else {
      draw(null);
    }
  }, [open, post, canvasNode]);

  if (!post) return null;

  const download = () => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `twatter-${post.id.slice(0, 8)}.png`;
    a.click();
    toast.success("Card downloaded.");
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/vent/${post.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard.");
    } catch {
      toast.error("Couldn't copy link.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="share-card-dialog"
        className="max-w-lg border"
        style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)", color: "var(--exhale-text)" }}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl" style={{ color: "var(--exhale-text)" }}>
            Share this vent
          </DialogTitle>
          <DialogDescription className="text-xs" style={{ color: "var(--exhale-text-muted)" }}>
            Download a dark card image or copy a link to this vent.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--exhale-border)" }}>
          <canvas ref={setCanvasRef} className="w-full h-auto block" data-testid="share-card-canvas" />
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Button
            onClick={copyLink}
            variant="outline"
            className="rounded-full"
            style={{ borderColor: "var(--exhale-border)", background: "var(--exhale-bg)", color: "var(--exhale-text)" }}
            data-testid="share-copy-link"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy link
          </Button>
          <Button
            onClick={download}
            className="rounded-full font-semibold"
            style={{ background: "var(--exhale-accent)", color: "#0C0B0E" }}
            data-testid="share-download-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download PNG
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
